// The voices on their own (Keaton 2026-10-31, api v4/narration.ts): record a scene's narrator and character lines (or
// the whole video's) before any clip, play them alone, or hear them over the picture. Recording is price first: the
// price is on the button and a second tap ("Record 3 lines, 12 credits?") is the yes; lines already recorded are reused
// free. Playing is free. The Director hears about every tap (actions.ts).
import { useState } from "react";
import * as act from "../actions";
import { api } from "../api";
import type { Project, SceneSoundPreview } from "../types";
import { Icon, Spinner, useLoad } from "./bits";

/** The controls' ids (parity.ts TAPS), per place: a scene's, or the whole video's on the preview. */
const IDS = {
  scene: { record: { dataAction: "scene.voices-record" }, yes: { dataAction: "scene.voices-record-yes" }, play: { dataAction: "scene.voices-play" }, picture: { dataAction: "scene.voices-picture" } },
  video: { record: { dataAction: "video.voices-record" }, yes: { dataAction: "video.voices-record-yes" }, play: { dataAction: "video.voices-play" }, picture: { dataAction: "video.voices-picture" } },
};

export function VoicesBar({ p, sceneId, index }: { p: Project; sceneId?: string; index?: number }) {
  const ids = sceneId ? [sceneId] : undefined;
  const where = sceneId ? `scene ${(index ?? 0) + 1}` : "the whole video";
  const id = sceneId ? IDS.scene : IDS.video;
  const { data: plan, reload } = useLoad(() => api.voicePrice(p.id, ids), [p.id, sceneId, JSON.stringify(sceneId ? (p.shots[sceneId] ?? null) : p.shots), JSON.stringify(p.script ?? null)]);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState<null | "record" | "play" | "picture">(null);
  const [pv, setPv] = useState<SceneSoundPreview | null>(null);
  const credits = plan?.credits ?? 0;
  const lines = plan?.newLines ?? 0;
  const recorded = plan?.reused ?? 0;
  const go = async <T,>(what: "record" | "play" | "picture", f: () => Promise<T>) => {
    setBusy(what);
    try {
      return await f();
    } finally {
      setBusy(null);
    }
  };
  if (!plan) return null;
  if (!lines && !recorded) return null; // nothing is spoken here
  return (
    <div className="detail-voices" data-help="voices">
      {lines > 0 && !asking && (
        <button data-action={id.record.dataAction} type="button" className="btn btn-quiet btn-small" disabled={!!busy || plan.scenes.some((s) => s.busy)} onClick={() => setAsking(true)} aria-label={`Record the voices for ${where}, ${credits} credits`}>
          <Icon name="mic" size={14} /> Record voices · {credits}
        </button>
      )}
      {asking && (
        <span className="detail-voices-ask">
          Record {lines} {lines === 1 ? "line" : "lines"} for {credits} credits?{recorded ? ` ${recorded} already recorded are reused free.` : ""}{" "}
          <button
            data-action={id.yes.dataAction}
            type="button"
            className="btn btn-primary btn-small"
            disabled={!!busy}
            onClick={() =>
              void go("record", async () => {
                const r = await act.recordVoices(p.id, ids ?? null, credits);
                setAsking(false);
                if (r) reload();
              })
            }
          >
            {busy === "record" ? <Spinner /> : null} Record
          </button>{" "}
          <button type="button" className="btn btn-quiet btn-small" onClick={() => setAsking(false)}>
            Not now
          </button>
        </span>
      )}
      {plan.scenes.some((s) => s.busy) && <span className="field-hint">Recording…</span>}
      {recorded > 0 && (
        <button data-action={id.play.dataAction} type="button" className="btn btn-quiet btn-small" disabled={!!busy} onClick={() => void go("play", () => act.playVoices(p.id, ids ?? null))} aria-label={`Play the voices for ${where}`}>
          {busy === "play" ? <Spinner /> : <Icon name="play" size={14} />} Play voices
        </button>
      )}
      {recorded > 0 && (
        <button
          data-action={id.picture.dataAction}
          type="button"
          className="btn btn-quiet btn-small"
          disabled={!!busy}
          aria-label={sceneId ? `Play scene ${(index ?? 0) + 1}'s voices over its picture` : "Play the voices over the pictures, the whole video"}
          onClick={() =>
            void go("picture", async () => {
              if (!sceneId) return void (await act.voicePass(p.id));
              const r = await act.previewSceneSound(p.id, sceneId, undefined, "voice");
              if (r?.url) setPv(r);
            })
          }
        >
          {busy === "picture" ? <Spinner /> : <Icon name="film" size={14} />} {sceneId ? "Over the picture" : "Over the pictures"}
        </button>
      )}
      {!busy && lines > 0 && recorded > 0 && <span className="field-hint">{lines} not recorded yet: they're left out when it plays.</span>}
      {pv?.url && <video src={pv.url} controls autoPlay playsInline aria-label={`Scene ${pv.sceneIndex}'s voices over its picture`} />}
    </div>
  );
}
